import { useState } from "react";
import type { I18nKey } from "../../../lib/language.js";
import { useI18n } from "../../../lib/useI18n.js";
import { MiniStepper } from "./bits.js";

interface Props {
  open: boolean;
  /** Reminder lead times, minutes before start. */
  offsets: number[];
  onChange: (next: number[]) => void;
}

const PRESETS: Array<{ minutes: number; labelKey: I18nKey; count?: number }> = [
  { minutes: 0, labelKey: "reminder.atTime" },
  { minutes: 5, labelKey: "reminder.minBefore", count: 5 },
  { minutes: 15, labelKey: "reminder.minBefore", count: 15 },
  { minutes: 60, labelKey: "reminder.hourBefore", count: 1 },
  { minutes: 1440, labelKey: "reminder.dayBefore" },
];
const PRESET_MINUTES = new Set(PRESETS.map((p) => p.minutes));

type Unit = "minutes" | "hours" | "days";
const UNIT_MINUTES: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 };
const UNIT_LABEL_KEY: Record<Unit, I18nKey> = {
  minutes: "reminder.minutes",
  hours: "reminder.hours",
  days: "reminder.days",
};

function customLabel(minutes: number, t: ReturnType<typeof useI18n>["t"]): string {
  if (minutes % 1440 === 0) {
    const d = minutes / 1440;
    return d === 1 ? t("reminder.dayBefore") : t("reminder.daysBefore", { count: d });
  }
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return t(h === 1 ? "reminder.hourBefore" : "reminder.hoursBefore", { count: h });
  }
  return t("reminder.minBefore", { count: minutes });
}

/** Reminder panel — slides open under the option row. Multi-select presets plus
 *  a custom "N units before" builder; each selected entry fires its own
 *  notification (stored as minutes-before offsets). */
export function ReminderPanel({ open, offsets, onChange }: Props) {
  const { t } = useI18n();
  const [amt, setAmt] = useState(10);
  const [unit, setUnit] = useState<Unit>("minutes");

  const customs = offsets.filter((m) => !PRESET_MINUTES.has(m));

  const toggle = (minutes: number) =>
    onChange(
      offsets.includes(minutes)
        ? offsets.filter((m) => m !== minutes)
        : [...offsets, minutes].sort((a, b) => a - b),
    );

  const addCustom = () => {
    const minutes = Math.max(1, amt) * UNIT_MINUTES[unit];
    if (!offsets.includes(minutes)) onChange([...offsets, minutes].sort((a, b) => a - b));
  };

  return (
    <div className={"reminder-panel" + (open ? " is-open" : "")} aria-hidden={!open}>
      <div className="reminder-inner">
        <div className="reminder-card">
          <div className="field">
            <div className="field-label">{t("reminder.remindMe")} <span className="lbl-hint">· {t("reminder.selectAny")}</span></div>
            <div className="reminder-chips">
              {PRESETS.map((p) => (
                <button
                  key={p.minutes}
                  type="button"
                  className={"rem-chip" + (offsets.includes(p.minutes) ? " is-on" : "")}
                  onClick={() => toggle(p.minutes)}
                >
                  <span className="rem-tick" aria-hidden="true">✓</span>
                  {t(p.labelKey, { count: p.count })}
                </button>
              ))}
              {customs.map((m) => (
                <span key={m} className="rem-chip is-on is-custom">
                  <span className="rem-tick" aria-hidden="true">✓</span>
                  {customLabel(m, t)}
                  <button
                    type="button"
                    className="rem-x"
                    onClick={() => onChange(offsets.filter((x) => x !== m))}
                    aria-label={t("reminder.remove")}
                  >×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="field custom-reminder">
            <div className="field-label">{t("reminder.custom")}</div>
            <div className="custom-row">
              <MiniStepper value={amt} onStep={(d) => setAmt((a) => Math.min(999, Math.max(1, a + d)))} />
              <div className="unit-seg" role="tablist">
                {(["minutes", "hours", "days"] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    className={unit === u ? "is-on" : ""}
                    onClick={() => setUnit(u)}
                  >{t(UNIT_LABEL_KEY[u])}</button>
                ))}
              </div>
              <span className="custom-suffix">{t("reminder.beforeStart")}</span>
              <button type="button" className="rem-add" onClick={addCustom}>+ {t("common.add")}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
